import { Button, Icon, Menu, MenuPlacement } from '@shinetools/lumen-react'
import { Columns3 } from 'lucide-react'
import { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import {
  EXPERT_TRIAL_BALANCE_COLUMN_IDS,
  EXPERT_TRIAL_BALANCE_COLUMN_LABEL_KEYS,
} from '../../constants/expertTrialBalanceColumnIds'
import type { ExpertTrialBalanceColumnId } from '../../hooks/useExpertTrialBalanceColumns'

interface ExpertTrialBalanceColumnsMenuProps {
  hiddenColumns: ExpertTrialBalanceColumnId[]
  setHiddenColumns: (hiddenColumnIds: ExpertTrialBalanceColumnId[]) => void
}

export const ExpertTrialBalanceColumnsMenu = ({
  hiddenColumns,
  setHiddenColumns,
}: ExpertTrialBalanceColumnsMenuProps): ReactElement => {
  const { t } = useTranslation()

  const hiddenColumnIdSet = useMemo(() => new Set(hiddenColumns), [hiddenColumns])
  const visibleColumnCount = EXPERT_TRIAL_BALANCE_COLUMN_IDS.length - hiddenColumns.length

  const handleColumnVisibilityChange = useCallback(
    (columnId: ExpertTrialBalanceColumnId, checked: boolean) => {
      const nextHiddenColumns = checked ? hiddenColumns.filter((id) => id !== columnId) : [...hiddenColumns, columnId]

      setHiddenColumns(nextHiddenColumns)
    },
    [hiddenColumns, setHiddenColumns],
  )

  return (
    <Menu
      aria-label={t('expert_trial_balance.toolbar.columns.label')}
      placement={MenuPlacement['bottom-end']}
      trigger={
        <Button
          aria-label={t('expert_trial_balance.toolbar.columns.label')}
          color="primary"
          icon={<Icon icon={Columns3} />}
          variant="outlined"
        />
      }
    >
      <Menu.Group label={t('expert_trial_balance.toolbar.columns.group')}>
        {EXPERT_TRIAL_BALANCE_COLUMN_IDS.map((columnId) => {
          const isVisible = !hiddenColumnIdSet.has(columnId)
          const isLastVisibleColumn = isVisible && visibleColumnCount === 1

          return (
            <Menu.Checkbox
              checked={isVisible}
              disabled={isLastVisibleColumn}
              key={columnId}
              label={t(EXPERT_TRIAL_BALANCE_COLUMN_LABEL_KEYS[columnId])}
              onCheckedChange={(checked) => handleColumnVisibilityChange(columnId, checked)}
            />
          )
        })}
      </Menu.Group>
    </Menu>
  )
}
